import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  ButtonGroup, 
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  ChevronLeft, 
  ChevronRight, 
  Today,
  // Add,
  CalendarMonth,
  ViewWeek,
  ViewDay
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CalendarView } from '../../schemas/calendar';

interface CalendarHeaderProps {
  currentDate: Date;
  view: CalendarView;
  onDateChange: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onCreateEvent: () => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  view,
  onDateChange,
  onViewChange,
  onPrevious,
  onNext,
  onToday,
  onCreateEvent
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const formatHeaderDate = () => {
    const date = dayjs(currentDate);
    switch (view) {
      case 'day':
        return date.format('dddd, MMMM D, YYYY');
      case 'week':
        const startOfWeek = date.startOf('week');
        const endOfWeek = date.endOf('week');
        return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D, YYYY')}`;
      case 'month':
        return date.format('MMMM YYYY');
      default:
        return date.format('MMMM YYYY');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: 1.5, md: 2 },
          p: { xs: 1.5, md: 2 },
          mx: { xs: 0, md: 2 },
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid',
          borderColor: 'grey.200',
          mb: { xs: 1.5, md: 3 }
        }}
      >
        {/* Left Section - Navigation */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Typography 
            variant={isMobile ? 'subtitle1' : 'h5'}
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mr: { xs: 0, md: 2 },
              flex: { xs: '1 1 100%', md: '0 1 auto' },
              lineHeight: 1.3,
            }}
          >
            {formatHeaderDate()}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Previous">
              <IconButton 
                onClick={onPrevious}
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.light',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Next">
              <IconButton 
                onClick={onNext}
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.light',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <ChevronRight />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Today">
              <Button
                onClick={onToday}
                startIcon={isMobile ? undefined : <Today />}
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  minWidth: { xs: 0, md: 'auto' },
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease',
                  ml: { xs: 0.5, md: 5 }
                }}
              >
                Today
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Center Section - Date Picker */}
        <Box sx={{ mx: { xs: 0, md: 3 }, width: { xs: '100%', md: 'auto' } }}>
          <DatePicker
            value={dayjs(currentDate)}
            onChange={(newValue) => newValue && onDateChange(newValue.toDate())}
            slotProps={{
              textField: {
                size: isMobile ? 'small' : 'medium',
                fullWidth: isMobile,
              },
            }}
            sx={{
              width: { xs: '100%', md: 'auto' },
              '& .MuiInputBase-root': {
                borderRadius: 2,
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }
            }}
          />
        </Box>

        {/* Right Section - View Controls & Create Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
          <ButtonGroup 
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              width: { xs: '100%', md: 'auto' },
              '& .MuiButton-root': {
                borderColor: 'grey.300',
                color: 'text.primary',
                flex: { xs: 1, md: 'none' },
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.light',
                  color: 'white'
                }
              },
              '& .MuiButton-root.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main'
              }
            }}
          >
            <Button
              disabled={view === 'day'}
              onClick={() => onViewChange('day')}
              startIcon={isMobile ? undefined : <ViewDay />}
            >
              Day
            </Button>
            <Button
              disabled={view === 'week'}
              onClick={() => onViewChange('week')}
              startIcon={isMobile ? undefined : <ViewWeek />}
            >
              Week
            </Button>
            <Button
              disabled={view === 'month'}
              onClick={() => onViewChange('month')}
              startIcon={isMobile ? undefined : <CalendarMonth />}
            >
              Month
            </Button>
          </ButtonGroup>

          {/* <Button
            onClick={onCreateEvent}
            variant="contained"
            startIcon={<Add />}
            sx={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(124, 58, 237, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 25px rgba(124, 58, 237, 0.4)'
              },
              transition: 'all 0.3s ease',
              fontWeight: 600
            }}
          >
            New Event
          </Button> */}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default CalendarHeader;